import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, resolveImg as resolve } from "./api";
import { BRAND, HOURS, TESTIMONIALS, SIGNATURES, MENU, GALLERY, REAL_PHOTOS, HERO_MEDIA, CHAPTERS, CELEBRATE, RIBBON } from "./data";
import { MENU_CATEGORIES } from "./menuData";
import { DEFAULT_TEXT } from "./textConfig";
import { DEFAULT_THEME } from "./theme";

export const DEFAULT_SETTINGS = {
  phone: BRAND.phone,
  address: BRAND.address,
  hours: HOURS,
  testimonials: TESTIMONIALS,
  images: {
    logo: "/brand/logo.png",
    heroVideo: HERO_MEDIA.video,
    hero: HERO_MEDIA.poster,
    storyMain: REAL_PHOTOS.family,
    storySecond: REAL_PHOTOS.bar,
    cultura: "/media/stock/cultura.jpg",
    visit: REAL_PHOTOS.fiesta,
  },
  theme: DEFAULT_THEME,
  signatures: SIGNATURES,
  gallery: GALLERY,
  chapters: CHAPTERS,
  celebrate: CELEBRATE,
  ribbon: RIBBON,
  topPicks: { eats: MENU.eats, drinks: MENU.drinks },
  seo: {
    title: "Blue Tequila | Mexican Restaurant, Grill & Cantina · North Port, FL",
    description:
      "Blue Tequila is a family-owned Mexican restaurant, grill & cantina in North Port, FL. Sizzling fajitas, hand-pressed tacos, birria and fresh-squeezed margaritas since 2016. Order online or visit us today.",
    keywords:
      "Mexican restaurant North Port, Mexican food North Port FL, tacos, fajitas, birria, quesabirrias, margaritas, cantina, Blue Tequila, best margaritas North Port, Mexican grill Tamiami Trail",
    ogImage: REAL_PHOTOS.diningBar,
    menuTitle: "Menu | Blue Tequila — Mexican Restaurant, Grill & Cantina",
    menuDescription:
      "Explore the full Blue Tequila menu — tacos, fajitas, birria, quesabirrias, ceviche, sizzling molcajetes and hand-crafted margaritas. Family owned in North Port, FL.",
    businessName: "Blue Tequila",
    priceRange: "$$",
    cuisine: "Mexican",
  },
  menus: {
    foodPdf: "/media/food-menu.pdf",
    drinksPdf: "/media/drinks-menu.pdf",
    drinksPages: [
      "/media/drinks/page-1.jpg",
      "/media/drinks/page-2.jpg",
      "/media/drinks/page-3.jpg",
      "/media/drinks/page-4.jpg",
      "/media/drinks/page-5.jpg",
      "/media/drinks/page-6.jpg",
      "/media/drinks/page-7.jpg",
      "/media/drinks/page-8.jpg",
    ],
  },
  happyHour: {
    enabled: true,
    image: "/media/stock/happy-hour.jpg",
    specials: [
      { name: "Sangrias", price: "$8.00" },
      { name: "Don Julio or Patrón Skinnies", price: "$8.99" },
      { name: "Domestic Drafts & House Wines", price: "$4.00" },
      { name: "Single Shot Well Drinks", price: "$5.00" },
      { name: "Grey Goose Single Shot", price: "$8.00" },
      { name: "House Margaritas", price: "$5.00" },
    ],
  },
  orderFab: { enabled: true },
  popups: [
    {
      id: "hh-default",
      enabled: true,
      page: "home",
      trigger: "load",
      delay: 1800,
      animation: "pop",
      frequency: "once",
      image: "/media/stock/happy-hour.jpg",
      title: "You still have time to Sip & Save!",
      subtitle: "Head on over for our Happy Hour specials — margaritas, sangrias, drafts & more.",
      ctaLabel: "See Happy Hour",
      ctaHref: "#happy-hour",
      schedule: { enabled: true, days: [0, 4, 5, 6], start: "11:00", end: "17:00" },
    },
  ],
  text: DEFAULT_TEXT,
};

export const DEFAULT_MENU = MENU_CATEGORIES;

const ContentContext = createContext(null);

export const ContentProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [menu, setMenu] = useState(DEFAULT_MENU);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, m] = await Promise.all([
        api.get("/content/settings"),
        api.get("/content/menu"),
      ]);
      if (s.data?.data) setSettings({ ...DEFAULT_SETTINGS, ...s.data.data, images: { ...DEFAULT_SETTINGS.images, ...(s.data.data.images || {}) }, menus: { ...DEFAULT_SETTINGS.menus, ...(s.data.data.menus || {}) }, happyHour: { ...DEFAULT_SETTINGS.happyHour, ...(s.data.data.happyHour || {}) }, orderFab: { ...DEFAULT_SETTINGS.orderFab, ...(s.data.data.orderFab || {}) }, theme: { ...DEFAULT_THEME, ...(s.data.data.theme || {}) }, text: { ...DEFAULT_TEXT, ...(s.data.data.text || {}) } });
      if (m.data?.data && Array.isArray(m.data.data) && m.data.data.length) setMenu(m.data.data);
    } catch (e) {
      // fall back to bundled defaults
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <ContentContext.Provider value={{ settings, menu, loading, refresh: load, resolveImg: resolve }}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => {
  const ctx = useContext(ContentContext);
  if (!ctx) return { settings: DEFAULT_SETTINGS, menu: DEFAULT_MENU, loading: false, refresh: () => {}, resolveImg: resolve };
  return ctx;
};
